import { effectiveTerminals } from "../effective";
import { v } from "./graph";
import type { ComponentDefinition } from "../types";
import type { Edge, Graph, Vertex } from "./graph";

/** Presentation edits are harmless; retained IDs are not proof of electrical
 * identity after a custom edit. This gate intentionally rejects added fields
 * that could alter conductor paths or current/protection assumptions. */
export function matchesCircuitDefinition(def: ComponentDefinition, baseline: ComponentDefinition): boolean {
  const identity = (part: ComponentDefinition) => ({
    category: part.category, manufacturer: part.manufacturer, model: part.model,
    terminals: part.terminals.map(({ id, polarity, direction, maxAmp }) => ({ id, polarity, direction, maxAmp }))
      .sort((a, b) => a.id.localeCompare(b.id)),
    internalDcConnections: part.internalDcConnections, oneWayDcConnections: part.oneWayDcConnections,
    isFuseBlock: part.isFuseBlock, fuseSlotType: part.fuseSlotType, renderVariant: part.renderVariant,
    fuseBusGroups: part.fuseBusGroups, dcBreaker: part.dcBreaker,
    compatibleVoltages: part.compatibleVoltages, voltage: part.voltage,
    ampDraw: part.ampDraw, ampOutput: part.ampOutput, acVoltage: part.acVoltage,
    pvMaxVoc: part.pvMaxVoc, currentShunt: part.currentShunt, bms: part.bms,
    simulateStates: part.simulateStates && {
      default: part.simulateStates.default,
      options: part.simulateStates.options.map(({ id, ampMultiplier, ampOverride, role, intraLinks }) =>
        ({ id, ampMultiplier, ampOverride, role, intraLinks })),
    },
  });
  return JSON.stringify(identity(def)) === JSON.stringify(identity(baseline));
}

const validVertices = new WeakMap<Graph, Set<Vertex>>();
function physicalVertices(graph: Graph): Set<Vertex> {
  const existing = validVertices.get(graph);
  if (existing) return existing;
  const vertices = new Set<Vertex>();
  for (const node of graph.nodes) {
    if (node.kind !== "component") continue;
    const def = graph.defLookup(node.definitionId, node.customDefinition);
    if (def) for (const post of effectiveTerminals(def, node)) vertices.add(v(node.id, post.id));
  }
  validVertices.set(graph, vertices);
  return vertices;
}

/** Walk only drawn conductors. The caller can open selected internal paths
 * without removing same-post cables, so a drawn bypass remains visible. */
export function circuitReach(
  graph: Graph, start: Vertex,
  include: (from: Vertex, edge: { vertex: Vertex; via: Edge }) => boolean = () => true,
): Set<Vertex> {
  const valid = physicalVertices(graph);
  if (!valid.has(start)) return new Set();
  const queue = [start];
  const seen = new Set(queue);
  for (let index = 0; index < queue.length; index++) {
    for (const edge of graph.adj.get(queue[index]) ?? []) {
      if (!valid.has(edge.vertex) || seen.has(edge.vertex) || !include(queue[index], edge)) continue;
      seen.add(edge.vertex);
      queue.push(edge.vertex);
    }
  }
  return seen;
}
